// One PUT of a Blob to a presigned S3 URL, with the bytes counted on the way out.
//
// fetch() reports no upload progress in any shipping browser, so XMLHttpRequest it is. Nothing
// but the body goes into the request: the presigned URL is signed over "host" only, and any
// header added here (Content-Type included) would have to be part of the signature. The type
// of the Blob itself is what XHR sends, which is why multipart parts are sliced without one.

export interface IPutXhr {
  url: string;
  body: Blob;
  // Fires many times a second. Deliberately raw: the caller owns the throttling, because it is
  // the only thing that knows what a re-render costs.
  onProgress: (loaded: number, total: number) => void;
  // Handed the live request before it is sent, so the caller can keep a handle to abort it
  onOpen: (xhr: XMLHttpRequest) => void;
}

// No ETag here on purpose: the server collects the part ETags itself with ListParts on
// complete, so nothing depends on the bucket CORS rule exposing the header.
export interface IPutXhrResult {
  status: number;
}

/**
 * Was this rejection a deliberate cancel rather than a failure?
 *
 * A property rather than `class UploadAbortError extends Error`: the app builds with
 * `target: "es5"`, where subclassing a built-in silently breaks `instanceof`.
 */
export function isUploadAbort(e: unknown): boolean {
  return !!(e as { isUploadAbort?: boolean } | null)?.isUploadAbort;
}

/** HTTP status behind a putXhr rejection: 0 for a network failure, -1 when it is not one at all. */
export function getPutStatus(e: unknown): number {
  const status = (e as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : -1;
}

function putError(message: string, status: number): Error {
  const error = new Error(message);
  (error as any).status = status;
  return error;
}

export default function putXhr({
  url,
  body,
  onProgress,
  onOpen,
}: IPutXhr): Promise<IPutXhrResult> {
  return new Promise<IPutXhrResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    // No timeout: a 16 MiB part on a slow uplink legitimately takes minutes. The engine watches
    // for stalls itself and aborts the request when progress stops.

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(putError(`S3 answered ${xhr.status}.`, xhr.status));
        return;
      }
      resolve({ status: xhr.status });
    };

    xhr.onerror = () => reject(putError("The connection to S3 dropped.", 0));
    xhr.ontimeout = () => reject(putError("The request to S3 timed out.", 0));
    xhr.onabort = () => {
      const error = new Error("Upload cancelled.");
      (error as any).isUploadAbort = true;
      reject(error);
    };

    onOpen(xhr);
    xhr.send(body);
  });
}
